import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Car, AlertCircle, ChevronRight, Tag, RefreshCw, CalendarDays, CalendarCheck, Search, RotateCcw } from 'lucide-react';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead } from '../services/notificationsApi';
import api from '../services/api';

const NotificationBell = ({ dropdownAlign = 'right' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [restoringIds, setRestoringIds] = useState(new Set());
  const dropdownRef = useRef(null);

  // Fetch unread count periodically
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const data = await getUnreadCount();
        setUnreadCount(data.count);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Every minute

    return () => clearInterval(interval);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_vehicle':
        return <Car className="h-5 w-5 text-emerald-500" />;
      case 'vehicle_sold':
        return <Tag className="h-5 w-5 text-orange-500" />;
      case 'dealer_sync':
        return <RefreshCw className="h-5 w-5 text-blue-500" />;
      case 'import_error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'offer_received':
        return <Tag className="h-5 w-5 text-indigo-500" />;
      case 'offer_accepted':
        return <Check className="h-5 w-5 text-emerald-500" />;
      case 'offer_rejected':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'offer_countered':
        return <Tag className="h-5 w-5 text-amber-500" />;
      case 'appointment_requested':
        return <CalendarDays className="h-5 w-5 text-indigo-500" />;
      case 'appointment_confirmed':
        return <Check className="h-5 w-5 text-emerald-500" />;
      case 'appointment_rejected':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'appointment_cancelled':
        return <AlertCircle className="h-5 w-5 text-muted" />;
      case 'appointment_feedback_request':
        return <CalendarCheck className="h-5 w-5 text-amber-500" />;
      case 'vehicle_reappeared':
        return <RotateCcw className="h-5 w-5 text-blue-500" />;
      case 'saved_search_match':
        return <Search className="h-5 w-5 text-indigo-500" />;
      default:
        return <Bell className="h-5 w-5 text-blue-500" />;
    }
  };

  const parseUTCDate = (dateString) => {
    // Backend stores datetime.utcnow() which is UTC but serialized without 'Z'
    // Ensure we treat it as UTC by appending 'Z' if no timezone indicator is present
    if (dateString && !dateString.endsWith('Z') && !dateString.includes('+')) {
      return new Date(dateString + 'Z');
    }
    return new Date(dateString);
  };

  const formatDate = (dateString) => {
    const date = parseUTCDate(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "A l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const formatFullDate = (dateString) => {
    const date = parseUTCDate(dateString);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleNotificationClick = async (notification) => {
    // Toggle expand
    if (expandedId === notification.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(notification.id);

    // Mark as read
    if (!notification.is_read) {
      try {
        await markAsRead(notification.id);
        setNotifications(notifications.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  const handleNavigateToVehicle = (vehicleId) => {
    setIsOpen(false);
    navigate(`/vehicles/${vehicleId}`);
  };

  const handleNavigateToStock = () => {
    setIsOpen(false);
    navigate('/stock');
  };

  const handleRestoreVehicles = async (notification) => {
    const vehicles = notification.data?.vehicles || [];
    if (vehicles.length === 0) return;

    setRestoringIds(prev => new Set([...prev, notification.id]));
    try {
      for (const v of vehicles) {
        await api.put(`/vehicles/${v.id}/stock-status`, { status: 'available' });
      }
      // Update notification in local state to reflect restoration
      setNotifications(notifications.map(n =>
        n.id === notification.id
          ? { ...n, data: { ...n.data, restored: true } }
          : n
      ));
    } catch (error) {
      console.error('Error restoring vehicles:', error);
    } finally {
      setRestoringIds(prev => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className="relative p-2 text-label hover:text-primary focus:outline-none"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={`absolute ${dropdownAlign === 'left' ? 'left-0' : 'right-0'} mt-2 w-80 bg-dark-card rounded-lg shadow-lg border border-dark-border z-50`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
            <h3 className="text-sm font-semibold text-primary">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-carmin hover:text-carmin-light flex items-center"
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Tout marquer comme lu
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-muted">
                Chargement...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted">
                Aucune notification
              </div>
            ) : (
              notifications.map((notification) => {
                const isExpanded = expandedId === notification.id;
                const vehicleData = notification.data?.vehicles
                  || (notification.data?.changes || []).map(c => ({ id: c.vehicle_id, make: c.make, model: c.model }));
                const hasVehicleLink = vehicleData && vehicleData.length > 0;

                return (
                  <div
                    key={notification.id}
                    className={`border-b border-dark-border transition-colors ${
                      !notification.is_read ? 'bg-carmin-50' : 'hover:bg-dark-hover'
                    }`}
                  >
                    {/* Clickable header */}
                    <button
                      onClick={() => handleNotificationClick(notification)}
                      className="w-full px-4 py-3 text-left"
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <p className={`text-sm ${!notification.is_read ? 'font-semibold' : ''} text-primary`}>
                            {notification.title}
                          </p>
                          {!isExpanded && (
                            <p className="text-sm text-secondary mt-0.5 truncate">
                              {notification.message}
                            </p>
                          )}
                          <p className="text-xs text-muted mt-0.5">
                            {formatDate(notification.created_at)}
                          </p>
                        </div>
                        <ChevronRight className={`h-4 w-4 text-secondary flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="px-4 pb-3 pl-12">
                        <p className="text-sm text-label whitespace-pre-wrap">
                          {notification.message}
                        </p>
                        <p className="text-xs text-muted mt-2">
                          {formatFullDate(notification.created_at)}
                        </p>

                        {/* Vehicle links */}
                        {hasVehicleLink && (
                          <div className="mt-2 space-y-1">
                            {vehicleData.map((v) => (
                              <button
                                key={v.id}
                                onClick={() => handleNavigateToVehicle(v.id)}
                                className="flex items-center text-sm text-carmin hover:text-carmin-light"
                              >
                                <Car className="h-3.5 w-3.5 mr-1.5" />
                                {v.make} {v.model}
                                <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Link to stock for new imports */}
                        {(notification.type === 'new_vehicle' || notification.type === 'price_change') && (
                          <button
                            onClick={handleNavigateToStock}
                            className="mt-2 flex items-center text-sm text-carmin hover:text-carmin-light"
                          >
                            Voir le stock
                            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                          </button>
                        )}

                        {/* Link to offers for offer notifications */}
                        {notification.type?.startsWith('offer_') && (
                          <button
                            onClick={() => { setIsOpen(false); navigate('/marketplace/offers'); }}
                            className="mt-2 flex items-center text-sm text-accent-purple hover:text-accent-purple"
                          >
                            Voir mes offres
                            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                          </button>
                        )}

                        {/* Link to appointments for appointment notifications */}
                        {notification.type?.startsWith('appointment_') && (
                          <button
                            onClick={() => { setIsOpen(false); navigate('/marketplace/appointments'); }}
                            className="mt-2 flex items-center text-sm text-accent-purple hover:text-accent-purple"
                          >
                            Voir mes rendez-vous
                            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                          </button>
                        )}

                        {/* Link to marketplace for saved search matches */}
                        {notification.type === 'saved_search_match' && (
                          <button
                            onClick={() => { setIsOpen(false); navigate('/marketplace'); }}
                            className="mt-2 flex items-center text-sm text-carmin hover:text-carmin-light"
                          >
                            Voir sur la marketplace
                            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                          </button>
                        )}

                        {/* Restore action for reappearing vehicles (manual_confirm mode) */}
                        {notification.type === 'vehicle_reappeared' && notification.data?.action === 'manual_confirm' && (
                          notification.data?.restored ? (
                            <p className="mt-2 text-sm text-emerald-400 flex items-center">
                              <Check className="h-3.5 w-3.5 mr-1.5" />
                              Remis en stock
                            </p>
                          ) : (
                            <button
                              onClick={() => handleRestoreVehicles(notification)}
                              disabled={restoringIds.has(notification.id)}
                              className="mt-2 flex items-center text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50"
                            >
                              <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${restoringIds.has(notification.id) ? 'animate-spin' : ''}`} />
                              {restoringIds.has(notification.id) ? 'Restauration...' : 'Remettre en stock'}
                            </button>
                          )
                        )}

                        {/* Info for auto-restored vehicles */}
                        {notification.type === 'vehicle_reappeared' && notification.data?.action === 'auto_restore' && (
                          <button
                            onClick={handleNavigateToStock}
                            className="mt-2 flex items-center text-sm text-carmin hover:text-carmin-light"
                          >
                            Voir le stock
                            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
